import { createCheckoutSession } from "@/api/privateRequests/stripe/createCheckoutSession";
import Button from "@/components/Button";
import { InputField } from "@/components/InputField";
import { VerificationCodeInput } from "@/components/atoms/VerificationCodeInput/VerificationCodeInput";
import { Button as DSButton } from "@/design-system/Button/Button";
import Input from "@/design-system/Input/Input";
import { arrow } from "@/designSystemIcons";
import { useEnter, useEscape } from "@/hooks/useKeyboardShortcut";
import { cn } from "@/lib/utils";
import { BREAKPOINTS } from "@/settings";
import {
  Trans,
  backendErrorCode,
  errorMessage,
  useTranslation,
} from "@repo/shared/i18n";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router";
import { useMediaQuery } from "usehooks-ts";

import {
  // appleLogin,
  confirmLoginWithCode,
  login,
  loginWithEmail,
} from "../../../../api/privateApi";
// import { useAppleLogin } from "../../../../hooks/useAppleLogin";
import { useGoogleLogin } from "../../../../hooks/useGoogleLogin";
import { addAlert, showModal } from "../../../../redux/message/messageSlice";
import { fetchSbcsSets } from "../../../../redux/sbcs/sbcSetsSlice";
import { AppDispatch } from "../../../../redux/store";
import {
  buyIntentSelector,
  clearPostLoginRedirect,
  fetchUser,
  postLoginRedirectSelector,
} from "../../../../redux/user/userSlice";

interface Props {
  onClose: () => void;
  handleLoginSuccess?: (method: "email" | "google" | "apple") => void;
  onScreenChange?: (screen: "email" | "code") => void;
  /** When true, swap the email input and Continue button for design-system equivalents. Pure UI; logic unchanged. */
  newDesign?: boolean;
  /** Sent on signup/confirm so the user is created with these favorites. */
  favoriteClubs?: number[];
  favoriteCountries?: number[];
}

/**
 * Imperative handle exposed via ref. Lets callers (e.g. an external "back"
 * button placed in a host shell like a bottom sheet) trigger the
 * code → email transition without duplicating LoginSection's internal logic.
 */
export interface LoginSectionHandle {
  goToEmailScreen: () => void;
}

const LOGIN_CODE_ERROR_TYPES: Record<
  string,
  "invalid" | "expired" | "too-many-failed-attempts"
> = {
  VERIFICATION_CODE_INVALID: "invalid",
  VERIFICATION_CODE_EXPIRED: "expired",
  TOO_MANY_FAILED_ATTEMPTS: "too-many-failed-attempts",
};

export const LoginSection = forwardRef<LoginSectionHandle, Props>(
  (
    {
      onClose,
      handleLoginSuccess,
      onScreenChange,
      newDesign,
      favoriteClubs,
      favoriteCountries,
    }: Props,
    ref,
  ) => {
    const { t } = useTranslation(["auth", "common"]);
    const dispatch = useDispatch<AppDispatch>();
    const [error, setError] = useState("");
    const [email, setEmail] = useState("");
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const googleButtonRef = useRef<HTMLDivElement>(null);
    const [continueClicked, setContinueClicked] = useState(false);
    const [isValidEmail, setIsValidEmail] = useState(false);
    const [showEmailInvalid, setShowEmailInvalid] = useState(false);
    const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
    const googleButtonWidth = useMemo(() => (isMobile ? 290 : 330), [isMobile]);
    const buyIntent = useSelector(buyIntentSelector);
    const postLoginRedirect = useSelector(postLoginRedirectSelector);
    const navigate = useNavigate();

    // Verification code state
    const [codeDigits, setCodeDigits] = useState<string[]>(Array(6).fill(""));
    const [codeError, setCodeError] = useState("");
    const [codeErrorType, setCodeErrorType] = useState<
      | "invalid"
      | "expired"
      | "too-many-requests"
      | "too-many-failed-attempts"
      | "unknown"
      | null
    >(null);
    const [isVerifying, setIsVerifying] = useState(false);
    const [clickedResend, setClickedResend] = useState(false);

    useEscape(onClose);

    /**
     * Everything that happens once a login lands, whichever way it landed.
     *
     * Email-code and Google finish in two different handlers that had grown
     * identical tails — which is how a step gets added to one and forgotten in
     * the other. Shared so there is one answer to "what happens after login".
     *
     * Returns the fetched user so callers can branch on it if they need to.
     */
    const completeLogin = useCallback(async () => {
      const res = await dispatch(fetchUser({ didLogin: true }))?.unwrap();
      if (buyIntent) {
        createCheckoutSession({
          priceId: buyIntent.subscriptionId,
          discountId: buyIntent.discountId,
          promoCode: buyIntent.promoCode,
        });
      }
      dispatch(fetchSbcsSets());
      if (res.user.firstTimeLogin) {
        dispatch(showModal({ modalType: "welcome" }));
      } else {
        dispatch(showModal());
      }
      /*
        After the modal call, so the welcome modal (a first-timer's) opens over
        the destination rather than over the page they left. Skipped when a
        buyIntent is in flight: that is already sending the browser to Stripe,
        and a client-side navigation racing it would be the more confusing of
        the two outcomes.
      */
      if (postLoginRedirect) {
        if (!buyIntent) navigate(postLoginRedirect);
        dispatch(clearPostLoginRedirect());
      }
      return res;
    }, [buyIntent, dispatch, navigate, postLoginRedirect]);

    const sendLoginEmail = useCallback(() => {
      loginWithEmail(email);
    }, [email]);

    const goToCodeScreen = () => {
      setContinueClicked(true);
      onScreenChange?.("code");
    };

    const goToEmailScreen = () => {
      setContinueClicked(false);
      setCodeDigits(Array(6).fill(""));
      setCodeError("");
      setCodeErrorType(null);
      setClickedResend(false);
      onScreenChange?.("email");
    };

    useImperativeHandle(ref, () => ({ goToEmailScreen }));

    const handleContinue = () => {
      if (isValidEmail) {
        goToCodeScreen();
        sendLoginEmail();
      } else {
        setShowEmailInvalid(true);
      }
    };

    useEnter(() => {
      if (!continueClicked) {
        handleContinue();
      }
    });

    const handleCodeComplete = async (code: string) => {
      setIsVerifying(true);
      setCodeError("");
      setCodeErrorType(null);
      try {
        await confirmLoginWithCode(
          email,
          code,
          favoriteClubs,
          favoriteCountries,
        );
        // After completeLogin, not before: fetchUser can still throw, and a
        // "Login successful" toast followed by an error is the worst of both.
        await completeLogin();
        dispatch(
          addAlert({
            description: t("login.loginSuccessful"),
            type: "success",
          }),
        );
        handleLoginSuccess?.("email");
      } catch (err: unknown) {
        const statusCode = (err as any)?.response?.status;
        const code = backendErrorCode(err);

        if (statusCode === 429) {
          setCodeErrorType("too-many-requests");
          setCodeError(t("login.codeError.tooManyRequests"));
        } else if (statusCode === 401) {
          const errorType = LOGIN_CODE_ERROR_TYPES[code ?? ""];
          if (errorType) {
            setCodeErrorType(errorType);
            setCodeError(errorMessage(code));
          } else {
            setCodeError(t("login.codeError.unknown"));
            setCodeErrorType("unknown");
          }
        }
        setCodeDigits(Array(6).fill(""));
      } finally {
        setIsVerifying(false);
      }
    };

    const handleResendCode = () => {
      if (clickedResend) return;
      sendLoginEmail();
      setClickedResend(true);
      setCodeError("");
      setCodeDigits(Array(6).fill(""));
      setTimeout(() => setClickedResend(false), 30000);
    };

    const onLoginSuccess = useCallback(async () => {
      await completeLogin();
      handleLoginSuccess?.("google");
    }, [completeLogin, handleLoginSuccess]);

    useGoogleLogin({
      onLogin: (credential) =>
        login(credential, favoriteClubs, favoriteCountries),
      onSuccess: onLoginSuccess,
      onFailure: setError,
      googleButtonRef: googleButtonRef,
      buttonWidth: googleButtonWidth,
    });

    /*
      Apple login, disabled. If it comes back it wants the same shape as the
      Google path above: await completeLogin(), then handleLoginSuccess("apple").
      (It used to carry its own commented copy of completeLogin; that has been
      removed now the real one is shared by both live paths.)
    */
    // const { signInWithApple, appleReady } = useAppleLogin({
    //   onLogin: appleLogin,
    //   onSuccess: async () => { await completeLogin(); handleLoginSuccess?.("apple"); },
    //   onFailure: setError,
    // });

    const handleEmailChange = (value: string) => {
      setIsValidEmail(emailRegex.test(value));
      setShowEmailInvalid(false);
      setEmail(value);
    };

    // Re-render Google button when returning from continueClicked view
    useEffect(() => {
      if (!continueClicked && window.google && googleButtonRef.current) {
        googleButtonRef.current.innerHTML = "";
        window.google.accounts?.id?.renderButton(googleButtonRef.current, {
          theme: "outline",
          type: "standard",
          text: "continue_with",
          width: googleButtonWidth,
        });
      }
    }, [continueClicked]);

    return (
      <section className="flex flex-col mt-6 gap-y-4 h-full pb-6">
        {continueClicked ? (
          <div
            className={cn(
              "flex flex-col h-full",
              newDesign ? "gap-y-2" : "gap-y-4",
            )}
          >
            {!newDesign && (
              <button
                className="absolute top-4 left-4 text-dark-tertiary w-3 h-3 rotate-90"
                onClick={(e) => {
                  e.stopPropagation();
                  goToEmailScreen();
                }}
              >
                {arrow}
              </button>
            )}

            <section
              className={cn(
                "flex flex-col gap-y-1 py-3 px-3 bg-inner-box rounded-sm text-sm",
                newDesign ? "bg-neutral-800 text-center" : "bg-inner-box",
              )}
            >
              <p className="text-dark-secondary">{t("login.sentEmailTo")}</p>
              <p className="font-semibold">{email}</p>
            </section>

            <div
              className={cn(
                (codeErrorType === "too-many-failed-attempts" ||
                  codeErrorType === "too-many-requests") &&
                  "hidden",
              )}
            >
              <p className="text-sm text-dark-secondary text-center my-4">
                {t("login.enterCode")}
              </p>
              <VerificationCodeInput
                value={codeDigits}
                onChange={setCodeDigits}
                onComplete={handleCodeComplete}
                error={!!codeError}
                disabled={isVerifying}
              />
            </div>
            <p
              className={cn(
                "text-xs -mt-1 mx-auto",
                codeError ? "text-error" : "text-dark-secondary",
              )}
            >
              {codeError || (isVerifying ? t("login.verifying") : "\u00A0")}
            </p>
            <p className="text-xs text-dark-secondary text-center">
              {t("login.checkSpamOrJunk")}
            </p>
            <section
              className={cn(
                "mt-auto flex flex-col gap-y-2 text-center",
                (codeErrorType === "too-many-failed-attempts" ||
                  codeErrorType === "too-many-requests") &&
                  "hidden",
              )}
            >
              <p className="text-xs text-dark-tertiary">
                {t("login.cantFindCode")}
                <button
                  className={cn(
                    clickedResend
                      ? "text-dark-tertiary cursor-default"
                      : "text-primary-action underline",
                  )}
                  onClick={handleResendCode}
                >
                  {clickedResend ? (
                    <span>{t("login.newCodeSent")}</span>
                  ) : (
                    <span>{t("login.requestNewCode")}</span>
                  )}
                </button>
              </p>
            </section>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-y-1">
              {newDesign ? (
                <Input
                  type="email"
                  value={email}
                  placeholder={t("login.emailPlaceholder")}
                  onChange={handleEmailChange}
                  size="medium"
                />
              ) : (
                <InputField
                  type="email"
                  value={email}
                  placeholder={t("login.emailPlaceholder")}
                  onChange={handleEmailChange}
                  dismissSearch={() => {}}
                  large={true}
                />
              )}

              {showEmailInvalid && (
                <div className="mx-auto text-sm text-error">
                  {t("login.invalidEmail")}
                </div>
              )}
            </div>
            {newDesign ? (
              <DSButton
                variant="primary"
                size="medium"
                stretch
                onClick={(e) => {
                  e.stopPropagation();
                  handleContinue();
                }}
              >
                {t("common:continue")}
              </DSButton>
            ) : (
              <Button
                label={t("common:continue")}
                onClick={(e) => {
                  e.stopPropagation();
                  handleContinue();
                }}
                variant="primary"
                large
              />
            )}
            <div className="flex flex-row items-center w-full gap-x-2">
              <div className="h-px w-full bg-dark-secondary" />
              <div className="text-sm text-dark-secondary">{t("login.or")}</div>
              <div className="h-px w-full bg-dark-secondary" />
            </div>
            <div className="flex flex-col">
              <div
                ref={googleButtonRef}
                className="w-[300px] h-[44px] mx-auto"
                style={{
                  minHeight: "44px",
                  minWidth: googleButtonWidth,
                }}
              />
              {error && (
                <div className="mx-auto text-sm text-error w-full mt-1">
                  {error}
                </div>
              )}
              {/* {appleReady && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  signInWithApple();
                }}
                className="mx-auto flex h-[44px] items-center justify-center gap-x-2 rounded border border-black/10 bg-white px-4 text-sm font-medium text-black"
                style={{ width: googleButtonWidth }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M16.365 1.43c0 1.14-.42 2.2-1.13 3.02-.84.97-2.21 1.72-3.36 1.63-.14-1.13.43-2.31 1.1-3.05.77-.86 2.16-1.51 3.39-1.6zM20.5 17.1c-.55 1.27-.82 1.84-1.53 2.96-1 1.57-2.4 3.52-4.14 3.53-1.55.02-1.95-.99-4.05-.98-2.1.01-2.54 1-4.09.98-1.74-.02-3.07-1.78-4.07-3.34C-.07 16.45-.36 11.1 2.06 8.27c1.16-1.35 2.99-2.2 4.6-2.2 1.64 0 2.68 1 4.04 1 1.32 0 2.13-1 4.03-1 1.43 0 2.95.78 4.03 2.13-3.54 1.94-2.96 6.99 1.74 8.9z" />
                </svg>
                <span>Sign in with Apple</span>
              </button>
            )} */}
              {/* Reserved slot so showing an error never overflows the
                fixed-height modal or shifts the layout. */}
              {/* <p className="mx-auto min-h-[1.25rem] w-full text-center text-sm text-error">
              {error}
            </p> */}
            </div>
            <p className="text-xs text-dark-secondary text-left mt-auto">
              <Trans
                ns="auth"
                i18nKey="login.acceptTerms"
                // New tab, or reading the terms throws away the login flow
                components={{
                  tos: (
                    <Link
                      className="text-dark-link"
                      to="/legal/tos"
                      target="_blank"
                      rel="noreferrer"
                    />
                  ),
                  privacy: (
                    <Link
                      className="text-dark-link"
                      to="/legal/privacy"
                      target="_blank"
                      rel="noreferrer"
                    />
                  ),
                }}
              />
            </p>
          </>
        )}
      </section>
    );
  },
);
LoginSection.displayName = "LoginSection";
